import { Spacing, Typography } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { omit } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import Pii from 'dashboard/components/Pii';
import { MonoSpan } from 'dashboard/components/Typography';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  CardContainer,
  Container,
  HeaderContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Event } from 'dashboard/models/event';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import WebhookEventModal from 'dashboard/views/DashboardEventsShowView/WebhookEventModal';
import WebhookEventsTable from 'dashboard/views/DashboardEventsShowView/WebhookEventsTable';
import Banner from 'design-system/components/Banner';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

interface DashboardEventsShowViewProps {
  webhookEventsLoading: boolean;
  event: Event;
  activeWebhookEventId: string | undefined;
  webhookEvents: WebhookEvent[];
  pageSize: number;
  paginationParams: PaginationParams;
  onPageSizeClick: (pageSize: number) => void;
  onPageLinkClick: (pageNumer: number) => void;
  onWebhookEventUpdate: (webhookEvent: WebhookEvent) => void;
  openModal: (webhookEvent: WebhookEvent) => void;
  closeModal: () => void;
}

const TAB_WIDTH = 2;

const DashboardEventsShowView = ({
  webhookEventsLoading,
  event,
  activeWebhookEventId,
  webhookEvents,
  pageSize,
  paginationParams,
  onPageSizeClick,
  onPageLinkClick,
  onWebhookEventUpdate,
  openModal,
  closeModal,
}: DashboardEventsShowViewProps) => {
  const eventWithoutRelationships = useMemo(() => omit('relationships', event), [event]);

  if (event == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t find that event.' backRoute='/dashboard/events' />
      </Backdrop>
    );
  }

  return (
    <>
      <Helmet title={`${event.id} - Events`} />
      <ModalWrapper isOpen={activeWebhookEventId != null} handleCloseModal={closeModal}>
        {activeWebhookEventId != null && (
          <WebhookEventModal
            webhookEventId={activeWebhookEventId}
            context='event'
            handleClose={closeModal}
            handleWebhookEventUpdate={onWebhookEventUpdate}
          />
        )}
      </ModalWrapper>
      <Backdrop>
        <Container>
          <HeaderContainer>
            <NavBack to='/dashboard/events' dataTest='event-show__nav-back'>
              All Events
            </NavBack>
          </HeaderContainer>

          <CardContainer>
            <Card>
              <Card.Body>
                <Card.Subtitle>Event ID</Card.Subtitle>
                <Card.Title>
                  <MonoSpan>{event.id}</MonoSpan>
                </Card.Title>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <Card.Subtitle>Event date</Card.Subtitle>
                <Card.Title>
                  {DateTime.fromISO(event.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
                </Card.Title>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <Card.Subtitle>Webhook events</Card.Subtitle>
                <Card.Title>{webhookEvents.length}</Card.Title>
              </Card.Body>
            </Card>
          </CardContainer>

          <Card>
            <Card.Body>
              <Card.Title>Event body</Card.Title>
              <Pii isRedacted={false}>
                <CodeHighlight id='events-show__code' language='json' rowsToShow={20}>
                  {JSON.stringify(eventWithoutRelationships, null, TAB_WIDTH)}
                </CodeHighlight>
              </Pii>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title>Webhook events</Card.Title>
              <StyledBanner variant='warning'>
                <CircleInfoIcon />
                Webhook events are subject to a 90 day retention policy. They are deleted 90 days
                after their creation and then are inaccessible via the Dashboard or API.
              </StyledBanner>
              <Spacer height={1} />
              <WebhookEventsTable
                hasUsedFilters={false}
                loading={webhookEventsLoading}
                webhookEvents={webhookEvents}
                dataTest='events-show__webhook-events-table'
                onRowClick={openModal}
                nullStateMessage='No webhooks have been triggered for this event.'
                context='event'
              />
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={onPageSizeClick}
                handlePageLinkClick={onPageLinkClick}
              />
            </Card.Body>
          </Card>
        </Container>
      </Backdrop>
    </>
  );
};

const StyledBanner = styled(Banner)`
  ${Typography.TextMedium};
  padding: ${Spacing[4]};
`;

export default DashboardEventsShowView;
